import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { LabelText } from "../Utils/Input";
import InputField from "../Utils/InputField";
import SelectField from "../Utils/SelectField";
import { Loader } from "../Utils/Loader";
import CommonButton from "../Utils/CommonButton";
import { CreatableSelectField } from "../Utils/CreatableSelectField";

import { fetchCountries } from "../../slices/customer/countries/thunk";
import { getLocationTypes } from "../../slices/Location/GetLocationType/thunk";
import { createLocation } from "../../slices/Location/AddLocation/thunk";
import { updateLocation } from "../../slices/Location/UpdateLocation/thunk";
import { fetchStates } from "../../slices/customer/states/thunk";
import { fetchCities } from "../../slices/customer/cities/thunk";

function LocationForm({ closeModal, onSuccess, selectedLocation }) {
  const dispatch = useDispatch();

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [callState, setCallState] = useState(false);
  const [callCity, setCallCity] = useState(false);

  const { countries } = useSelector((state) => state.Countries || {});
  const { locationTypes } = useSelector((state) => state.GetLocationType || {});

  const [values, setValues] = useState({
    name: "",
    code: "",
    // type: "",
    addressLine1: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    latitude: "",
    longitude: "",
  });

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(
      fetchCountries(
        () => {},
        () => {},
      ),
    );

    dispatch(getLocationTypes());
  }, [dispatch]);

  // INPUT CHANGE

  const handleChange = (e) => {
    const { name, value } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // COUNTRY CHANGE

  const onCountryChange = (value) => {
    setSelectedCountry(value);

    setSelectedState("");
    setSelectedCity("");

    setStates([]);
    setCities([]);

    setValues((prev) => ({
      ...prev,
      country: value,
      state: "",
      city: "",
    }));

    setErrors((prev) => ({
      ...prev,
      country: "",
    }));

    setCallState(true);
  };

  // STATE CHANGE

  const onStateChange = (value) => {
    setSelectedState(value);

    setSelectedCity("");

    setCities([]);

    setValues((prev) => ({
      ...prev,
      state: value,
      city: "",
    }));

    setErrors((prev) => ({
      ...prev,
      state: "",
    }));

    setCallCity(true);
  };

  // CITY CHANGE

  const onCityChange = (value) => {
    setSelectedCity(value);

    setValues((prev) => ({
      ...prev,
      city: value,
    }));

    setErrors((prev) => ({
      ...prev,
      city: "",
    }));
  };

  // FETCH STATES

  useEffect(() => {
    if (callState && selectedCountry) {
      dispatch(
        fetchStates(selectedCountry, setCallCity, "isOperator", (data) => {
          const options = data?.options || [];

          setStates(
            options.map((item) => ({
              label: item.label || item,
              value: item.value || item,
            })),
          );
        }),
      );

      setCallState(false);
    }
  }, [callState, selectedCountry, dispatch]);

  // FETCH CITIES

  useEffect(() => {
    if (callCity && selectedCountry && selectedState) {
      dispatch(
        fetchCities(selectedCountry, selectedState, "isOperator", (data) => {
          const options = data?.cities || data?.options || data || [];

          setCities(
            options.map((item) => ({
              label: item.label || item,
              value: item.value || item,
            })),
          );
        }),
      );

      setCallCity(false);
    }
  }, [callCity, selectedCountry, selectedState, dispatch]);

  // EDIT MODE

  useEffect(() => {
    if (selectedLocation) {
      // const typeObj = selectedLocation?.type
      //   ? {
      //       label: selectedLocation.type,
      //       value: selectedLocation.type,
      //     }
      //   : null;

      setValues({
        name: selectedLocation?.name || "",
        code: selectedLocation?.code || "",
        // type: typeObj,
        addressLine1: selectedLocation?.address?.line1 || "",
        city: selectedLocation?.address?.city || "",
        state: selectedLocation?.address?.state || "",
        country: selectedLocation?.address?.country || "",
        pincode: selectedLocation?.address?.pincode || "",
        latitude: selectedLocation?.address?.latitude || "",
        longitude: selectedLocation?.address?.longitude || "",
      });

      setSelectedCountry({
        label: selectedLocation?.address?.country,
        value: selectedLocation?.address?.country,
      });

      setSelectedState({
        label: selectedLocation?.address?.state,
        value: selectedLocation?.address?.state,
      });

      setSelectedCity({
        label: selectedLocation?.address?.city,
        value: selectedLocation?.address?.city,
      });

      setCallState(true);
      setCallCity(true);
    }
  }, [selectedLocation]);

  // VALIDATION

  const validate = () => {
    let newErrors = {};

    if (!values.name.trim()) {
      newErrors.name = "Location Name is required";
    }

    if (!values.code.trim()) {
      newErrors.code = "Code is required";
    }

    // if (!values.type) {
    //   newErrors.type = "Location Type is required";
    // }

    if (!values.addressLine1.trim()) {
      newErrors.addressLine1 = "Address is required";
    }

    if (!values.country) {
      newErrors.country = "Country is required";
    }

    if (!values.state) {
      newErrors.state = "State is required";
    }

    if (!values.city) {
      newErrors.city = "City is required";
    }

    return newErrors;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fill mandatory fields");

      return;
    }

    const payload = {
      name: values.name,
      code: values.code,
      // type: values.type?.value || values.type,
      address: {
        line1: values.addressLine1,
        city: values.city?.value || values.city,
        state: values.state?.value || values.state,
        country: values.country?.value || values.country,
        pincode: values.pincode,
        latitude: Number(values.latitude) || 0.00,
        longitude: Number(values.longitude) || 0.00,
      },
      isActive: true,
    };

    const callback = () => {
      closeModal();
      onSuccess?.();
    };

    setLoading(true);

    if (selectedLocation?.locationid) {
      dispatch(
        updateLocation(
          payload,
          selectedLocation.locationid,
          setLoading,
          callback,
        ),
      );
    } else {
      dispatch(createLocation(payload, setLoading, onSuccess, callback));
    }
  };

  return (
    <>
      {loading && <Loader />}

      <Row>
        {/* NAME */}
        <Col md={6}>
          <LabelText
            htmlFor={"name"}
            className={"text-danger"}
            displayText={"Location Name"}
            important={"*"}
          />

          <InputField
            name="name"
            value={values.name}
            placeholder={"Enter location name"}
            handleChange={handleChange}
            invalid={errors.name ? true : false}
          />
        </Col>

        {/* CODE */}
        <Col md={6}>
          <LabelText
            htmlFor={"code"}
            className={"text-danger"}
            displayText={"Code"}
            important={"*"}
          />

          <InputField
            name="code"
            value={values.code}
            handleChange={handleChange}
            placeholder={"Enter location code"}
            invalid={errors.code ? true : false}
          />
        </Col>

        {/* TYPE */}
        {/* <Col md={4}>
          <LabelText
            htmlFor={"type"}
            className={"text-danger"}
            displayText={"Location Type"}
            important={"*"}
          />

          <CreatableSelectField
            name="type"
            values={values.type}
            className={errors.type ? "error-input" : ""}
            handleChange={(val) => {
              setValues((prev) => ({
                ...prev,
                type: val,
              }));

              setErrors((prev) => ({
                ...prev,
                type: "",
              }));
            }}
            options={
              locationTypes?.type?.map((item) => ({
                label: item.charAt(0).toUpperCase() + item.slice(1),
                value: item,
              })) || []
            }
            placeholder="Location Type"
            isClearable={true}
            isRVStyle={true}
          />
        </Col> */}

        {/* ADDRESS */}
        <Col md={12} className="mt-3">
          <LabelText
            htmlFor={"addressLine1"}
            displayText="Address"
            className={"text-danger"}
            important={"*"}
          />

          <InputField
            name="addressLine1"
            value={values.addressLine1}
            type="textarea"
            handleChange={handleChange}
            placeholder={"Enter address"}
            invalid={errors.addressLine1 ? true : false}
          />
        </Col>

        {/* COUNTRY */}
        <Col md={6} className="mt-3">
          <LabelText
            htmlFor={"country"}
            displayText="Country"
            important={"*"}
            className={"text-danger"}
          />

          <SelectField
            className={errors.country ? "error-input" : ""}
            value={selectedCountry}
            handleChange={onCountryChange}
            options={(countries?.countries || []).map((country) => ({
              label: country,
              value: country,
            }))}
            placeholder={"Select Country"}
            isClearable={true}
            isRVStyle={true}
          />
        </Col>

        {/* STATE */}
        <Col md={6} className="mt-3">
          <LabelText
            htmlFor={"state"}
            displayText="State"
            important={"*"}
            className={"text-danger"}
          />

          <SelectField
            className={errors.state ? "error-input" : ""}
            value={selectedState}
            handleChange={onStateChange}
            options={states}
            placeholder={"Select State"}
            isClearable={true}
            isDisabled={!selectedCountry}
            isRVStyle={true}
          />
        </Col>

        {/* CITY */}
        <Col md={6} className="mt-3">
          <LabelText
            htmlFor={"city"}
            displayText="City"
            important={"*"}
            className={"text-danger"}
          />

          <SelectField
            className={errors.city ? "error-input" : ""}
            value={selectedCity}
            handleChange={onCityChange}
            options={cities}
            placeholder={"Select City"}
            isClearable={true}
            isDisabled={!selectedState}
            isRVStyle={true}
          />
        </Col>

        {/* LATITUDE */}

        {/* <Col md={6} className="mt-3">
          <LabelText displayText="Latitude" />

          <InputField
            name="latitude"
            value={values.latitude}
            handleChange={handleChange}
          />
        </Col> */}

        {/* LONGITUDE */}

        {/* <Col md={6} className="mt-3">
          <LabelText displayText="Longitude" />

          <InputField
            name="longitude"
            value={values.longitude}
            handleChange={handleChange}
          />
        </Col> */}

        {/* PINCODE */}
        <Col md={6} className="mt-3">
          <LabelText displayText="Pincode" />
          <InputField
            name="pincode"
            value={values.pincode}
            handleChange={handleChange}
          />
        </Col>

        {/* BUTTONS */}

     <div
          className="d-flex justify-content-center w-100 gap-2 flex-wrap pe-1 py-3"
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #e9ebec",
            marginTop: "20px",
          }}
        >
          <div className="d-flex gap-2 justify-content-center">
            <CommonButton
              outline={true}
              className={
                "btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
              }
              color={"main-color"}
              type={"reset"}
              buttonAction={() => {
                closeModal();
              }}
              buttonText={"Cancel"}
              icon={
                <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-2"></i>
              }
              btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
            />

            <CommonButton
              color={"main-color"}
              className={
                "btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
              }
              icon={
                <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-2"></i>
              }
              btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
              type={"submit"}
              buttonText={"Submit"}
              buttonAction={handleSubmit}
            />
          </div>
        </div>
      </Row>
    </>
  );
}

export default LocationForm;
